import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import tzLookup from "tz-lookup";
import moment from "moment-timezone";
import { Eye, EyeOff } from "lucide-react";
import { signInWithPopup, GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import FormattedDateInput from "../../components/Forms/FormattedDateInput";
import { auth, googleProvider, appleProvider } from "../../config/firebase";
import { updateProfile } from "../../services/api";
import googleLogo from "../../assets/google-icon-logo-svgrepo-com.svg";
import appleLogo from "../../assets/apple-black-logo-svgrepo-com.svg";
import "./Auth.css";

const LIBRARIES = ["places"];

const Signup = () => {
  const navigate = useNavigate();
  const { signup, verifyEmail, authLoading, resendOtp, loginWithGoogle, loginWithApple, user } = useAuth();
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    date: "",
    time: "",
    locationName: "",
    timezone: "",
  });
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(30);
  const timerRef = useRef(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  const autocompleteRef = useRef(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (user) {
      navigate(user?.isProfileComplete ? "/app" : "/profile", { replace: true });
    }
  }, [user, navigate]);

  // Set default timezone based on browser
  useEffect(() => {
    if (Intl?.DateTimeFormat && !formValues.timezone) {
      const offsetMinutes = new Date().getTimezoneOffset();
      const sign = offsetMinutes <= 0 ? "+" : "-";
      const absMinutes = Math.abs(offsetMinutes);
      const hours = Math.floor(absMinutes / 60);
      const minutes = absMinutes % 60;
      const formatted = `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      setFormValues((prev) => ({ ...prev, timezone: formatted }));
    }
  }, [formValues.timezone]);

  useEffect(() => {
    if (showOtpModal && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [showOtpModal, timer]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const minutesToOffsetString = (offsetMinutes) => {
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const roundToNearest30Min = (offsetMinutes) => {
    const sign = offsetMinutes >= 0 ? 1 : -1;
    const abs = Math.abs(offsetMinutes);
    const rounded = Math.round(abs / 30) * 30;
    return sign * rounded;
  };

  const deriveTimezoneOffset = (lat, lng, dateVal, timeVal) => {
    try {
      const tzName = tzLookup(lat, lng);

      let dateStr = dateVal;
      let timeStr = timeVal;

      if (!dateStr) {
        const now = moment.tz(tzName);
        dateStr = now.format("YYYY-MM-DD");
        timeStr = timeStr || now.format("HH:mm");
      } else {
        timeStr = timeStr || "12:00";
      }

      const m = moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", tzName);
      if (!m.isValid()) {
        const now = moment.tz(tzName);
        const offsetMin = now.utcOffset();
        return minutesToOffsetString(offsetMin);
      }

      const offsetMin = m.utcOffset();
      const absOffset = Math.abs(offsetMin);
      const remainder = absOffset % 30;

      let finalOffset = offsetMin;
      if (remainder !== 0) {
        finalOffset = roundToNearest30Min(offsetMin);
      }

      return minutesToOffsetString(finalOffset);
    } catch (e) {
      console.error("Error deriving timezone offset:", e);
      return null;
    }
  };

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place || !place.geometry) return;

    const { lat, lng } = place.geometry.location;
    const description = place.formatted_address || place.name || formValues.locationName;

    const newSelectedPlace = {
      lat: lat(),
      lng: lng(),
      description,
    };

    setSelectedPlace(newSelectedPlace);
    setFormValues((prev) => ({ ...prev, locationName: description }));

    const derivedOffset = deriveTimezoneOffset(
      newSelectedPlace.lat,
      newSelectedPlace.lng,
      formValues.date,
      formValues.time
    );

    if (derivedOffset) {
      setFormValues((prev) => ({ ...prev, timezone: derivedOffset }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);
    setSuccessMessage(null);

    if (formValues.password !== formValues.confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    if (!selectedPlace) {
      setFormError("Please select a location from the suggestions.");
      return;
    }

    try {
      // Parse date components
      const [year, month, day] = formValues.date.split("-").map(Number);
      const [hour, minute] = formValues.time.split(":").map(Number);

      await signup({
        name: formValues.name,
        email: formValues.email,
        password: formValues.password,
        date: { year, month, day },
        time: { hour, minute, second: 0 },
        timezone: formValues.timezone,
        location: {
          name: formValues.locationName,
          latitude: selectedPlace.lat,
          longitude: selectedPlace.lng,
        },
      });

      setSuccessMessage("Account created! Please verify your email.");
      setShowOtpModal(true);
      setTimer(30);
    } catch (error) {
      setFormError(error?.message || "Unable to create your account.");
    }
  };

  const handleResendOtp = async () => {
    setFormError(null);
    try {
      await resendOtp({ email: formValues.email });
      setSuccessMessage("New OTP sent!");
      setTimer(30);
    } catch (error) {
      // Even if it fails (endpoint doesn't exist), we show timer logic for UX as requested.
      // But typically we should show error.
      setFormError(error?.message || "Failed to resend OTP");
    }
  };

  const handleGoogleLogin = useCallback(() => {
    setGoogleLoading(true);
    signInWithPopup(auth, googleProvider)
      .then(async (result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const tokenId = credential?.idToken;
        const fullName = result?.user?.displayName || undefined;
        const email = result?.user?.email || undefined;
        if (!tokenId) {
          throw new Error("Google token not found");
        }
        const loggedInUser = await loginWithGoogle({
          tokenId,
          name: fullName,
          email,
          provider: "google",
        });

        if (!loggedInUser?.name && fullName) {
          try {
            await updateProfile({ name: fullName });
          } catch (_error) {
            // Ignore non-blocking name sync errors.
          }
        }
      })
      .catch((error) => {
        const message = error?.message || "";
        if (!/cancel/i.test(message) && !/popup/i.test(message)) {
          setFormError(error?.message || "Google sign-in failed. Please try again.");
        }
        setGoogleLoading(false);
      })
      .finally(() => {
        setGoogleLoading(false);
      });
  }, [loginWithGoogle]);

  const handleAppleLogin = useCallback(async () => {
    setAppleLoading(true);

    try {
      const result = await signInWithPopup(auth, appleProvider);
      const credential = OAuthProvider.credentialFromResult(result);
      const appleId = credential?.idToken || result?.user?.uid;
      const email = result?.user?.email || undefined;
      const name = result?.user?.displayName || undefined;

      if (!appleId) {
        throw new Error("Apple token not found");
      }

      await loginWithApple({ appleId, email, name });
    } catch (error) {
      const message = error?.message || "";
      if (!/popup/i.test(message) && !/cancel/i.test(message)) {
        if (error?.code === "auth/operation-not-allowed") {
          setFormError("Apple Sign-In is not enabled in Firebase Auth for this project.");
        } else {
          setFormError(error?.message || "Apple sign-in failed. Please try again.");
        }
      }
    } finally {
      setAppleLoading(false);
    }
  }, [loginWithApple]);

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      await verifyEmail({ email: formValues.email, otp });
      setSuccessMessage("Email verified! Redirecting...");
      setTimeout(() => navigate("/app", { replace: true }), 1500);
    } catch (error) {
      setFormError(error?.message || "Invalid or expired OTP");
    }
  };

  if (loadError) return <div className="auth-card">Error loading maps</div>;

  return (
    <div className="auth-card">
      <h1>Create an account</h1>
      <form className="auth-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input
            type="text"
            name="name"
            placeholder="John Doe"
            value={formValues.name}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          Email
          <input
            type="email"
            name="email"
            placeholder="you@example.com"
            required
            value={formValues.email}
            onChange={handleChange}
          />
        </label>
        <label>
          Password
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="********"
              required
              value={formValues.password}
              onChange={handleChange}
              className="w-full !pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>
        <label>
          Confirm password
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="********"
              required
              value={formValues.confirmPassword}
              onChange={handleChange}
              className="w-full !pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 focus:outline-none"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <label style={{ flex: 1 }}>
            Date of Birth
            <FormattedDateInput
              name="date"
              required
              value={formValues.date}
              onChange={handleChange}
            />
          </label>
          <label style={{ flex: 1 }}>
            Time of Birth
            <input
              type="time"
              name="time"
              required
              value={formValues.time}
              onChange={handleChange}
            />
          </label>
        </div>

        <label>
          Place of Birth
          {!isLoaded ? (
            <input
              type="text"
              placeholder="Loading maps..."
              disabled
              className="w-full"
            />
          ) : (
            <Autocomplete
              onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
              onPlaceChanged={handlePlaceSelect}
            >
              <input
                type="text"
                name="locationName"
                placeholder="City, Country"
                required
                value={formValues.locationName}
                onChange={handleChange}
                className="w-full"
                onKeyDown={(e) => {
                  // Prevent form submission on Enter in the autocomplete field
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
            </Autocomplete>
          )}
        </label>

        {/* Hidden or Read-only Timezone field if needed, or just let users trust the auto-detect */}
        {formValues.timezone && (
          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '-0.5rem', marginBottom: '1rem' }}>
            Timezone: UTC{formValues.timezone}
          </p>
        )}

        {formError && <p className="auth-error">{formError}</p>}
        {successMessage && <p className="auth-success">{successMessage}</p>}
        <button
          type="submit"
          className={`auth-submit ${authLoading ? 'loading' : ''}`}
          disabled={authLoading || googleLoading || appleLoading}
        >
          {authLoading ? "Creating..." : "Create account"}
        </button>
      </form>

      <div className="auth-social-separator">Or continue with</div>
      <div className="auth-social-buttons">
        <button
          type="button"
          className="auth-social-button google"
          onClick={handleGoogleLogin}
          disabled={authLoading || googleLoading || appleLoading}
        >
          <span className="auth-social-icon google" aria-hidden="true">
            <img src={googleLogo} alt="" width={20} height={20} />
          </span>
          <span>{googleLoading ? "Connecting to Google..." : "Continue with Google"}</span>
        </button>
        <button
          type="button"
          className="auth-social-button apple"
          onClick={handleAppleLogin}
          disabled={authLoading || googleLoading || appleLoading}
        >
          <span className="auth-social-icon apple" aria-hidden="true">
            <img src={appleLogo} alt="" width={20} height={20} />
          </span>
          <span>{appleLoading ? "Connecting to Apple..." : "Continue with Apple"}</span>
        </button>
      </div>

      <p className="auth-note">
        Already registered?{" "}
        <Link to="/login" className="auth-link">
          Sign in instead.
        </Link>
      </p>

      <p className="text-xs text-slate-400 mt-2 text-center leading-relaxed">
        By signing up, you agree to our{" "}
        <Link
          to="/terms-conditions"
          className="text-indigo-500 hover:text-indigo-700 underline underline-offset-2"
        >
          Terms &amp; Conditions
        </Link>{" "}
        and{" "}
        <Link
          to="/privacy-policy"
          className="text-indigo-500 hover:text-indigo-700 underline underline-offset-2"
        >
          Privacy Policy
        </Link>
        .
      </p>

      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl relative">
            <h2 className="text-xl font-bold mb-4 text-slate-800">Verify Email</h2>
            <p className="text-slate-600 mb-6">
              Please enter the 6-digit verification code sent to <strong>{formValues.email}</strong>
            </p>
            <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest p-3 rounded-lg border border-slate-300 focus:border-indigo-500 outline-none"
                required
              />
              {formError && <p className="text-red-600 text-sm bg-red-50 p-2 rounded">{formError}</p>}
              <button
                type="submit"
                className="bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                disabled={authLoading}
              >
                {authLoading ? "Verifying..." : "Verify & Continue"}
              </button>
            </form>

            <div className="mt-4 flex justify-between items-center text-sm">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={timer > 0 || authLoading}
                className="text-indigo-600 hover:text-indigo-800 disabled:text-slate-400 font-medium"
              >
                {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
              </button>
              <button
                type="button"
                onClick={() => setShowOtpModal(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;

